import { useState } from "react";
import { useAvailableChatPartners } from "@/hooks/queries/useAvailableChatPartners";
import { useCreateMessage } from "@/hooks/queries/useMessages";
import { formatImageUrl, getInitials } from "@/utils/formatters";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ChatBubbleLeftRightIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { getSecureUser } from "@/utils/secureStorage";

interface AvailableChatPartnersProps {
  onPartnerSelect?: (partnerId: string) => void;
}

const AvailableChatPartners = ({ onPartnerSelect }: AvailableChatPartnersProps) => {
  const { t } = useTranslation();
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  
  const { data: partners, isLoading, error } = useAvailableChatPartners();
  const createMessageMutation = useCreateMessage();
  const user = getSecureUser();

  const handleSendMessage = async (partnerId: string, partnerType: "User" | "Company") => {
    if (!messageText.trim() || !user?.id) return;

    try {
      await createMessageMutation.mutateAsync({
        sender: user.id,
        recipient: partnerId,
        senderModel: "User",
        recipientModel: partnerType,
        content: { text: messageText.trim() }
      });
      
      setMessageText("");
      onPartnerSelect?.(partnerId);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400">
          {t("messaging.errorLoadingPartners", "Error loading chat partners")}
        </p>
      </div>
    );
  }

  if (!partners || partners.length === 0) {
    return (
      <div className="text-center py-8 px-4">
        <ChatBubbleLeftRightIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
        <p className="mt-2 text-xs sm:text-sm text-gray-500">
          {t("messaging.noAvailablePartners", "No available chat partners")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
          {t("messaging.availablePartners", "Available Chat Partners")}
        </h3>
        <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          {partners.length} {t("messaging.partners", "partners")}
        </span>
      </div>

      <div className="space-y-2 sm:space-y-3">
        {partners.map((partner) => (
          <motion.div
            key={partner.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`border rounded-lg p-4 transition-colors ${
              selectedPartner === partner.id
                ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* Profile Image */}
                <div className="flex-shrink-0">
                  {partner.profilePhoto ? (
                    <img
                      src={formatImageUrl(partner.profilePhoto)}
                      alt={partner.displayName}
                      className="h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  ) : null}
                  
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 ${
                      partner.profilePhoto ? "hidden" : ""
                    }`}
                  >
                    <span className="text-sm font-medium">
                      {getInitials(partner.displayName)}
                    </span>
                  </div>
                </div>

                {/* Partner Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {partner.displayName}
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {partner.type}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {partner.id}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() => setSelectedPartner(selectedPartner === partner.id ? null : partner.id)}
                className="flex items-center px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                {selectedPartner === partner.id ? t("messaging.cancel", "Cancel") : t("messaging.chat", "Chat")}
              </button>
            </div>

            {/* Message Input (shown when selected) */}
            {selectedPartner === partner.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
              >
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder={t("messaging.typeMessage", "Type your message...")}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSendMessage(partner.id, partner.type);
                      }
                    }}
                  />
                  <button
                    onClick={() => handleSendMessage(partner.id, partner.type)}
                    disabled={!messageText.trim() || createMessageMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                  >
                    {createMessageMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                        {t("messaging.send", "Send")}
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AvailableChatPartners;
